import React, { useState, useEffect } from 'react';
import { X, FileSearch, FileSignature, Handshake, Scale, CheckCircle, AlertTriangle, Banknote, TrendingDown, TrendingUp, Building2, Users, Truck } from 'lucide-react';
import useGameStore from '../../store/gameStore';
import { useTranslation } from '../../i18n';
import { ACQUISITION_STAGES } from '../../data/acquisitionStages';
import { computeAskingPrice, evaluateNegotiation } from '../../utils/acquisition';
import { COMPETITORS } from '../../data/competitors';
import DAFCommentary from './DAFCommentary';
import MarkdownLite from '../MarkdownLite';
import { depotBrandPhaseImage } from '../../data/depotPhases';

const STAGE_ICONS = {
  1: FileSearch,
  2: FileSignature,
  3: Handshake,
  4: Scale,
  5: CheckCircle,
};

/**
 * 5-stage M&A wizard. Mounted globally; opens whenever pendingAcquisition or
 * lastAcquisitionSummary is non-null.
 */
export default function AcquisitionModal() {
  const { t } = useTranslation();
  const pending = useGameStore(s => s.pendingAcquisition);
  const summary = useGameStore(s => s.lastAcquisitionSummary);
  const advance = useGameStore(s => s.advanceAcquisitionStage);
  const cancel = useGameStore(s => s.cancelAcquisition);
  const complete = useGameStore(s => s.completeAcquisition);
  const reputation = useGameStore(s => s.reputation || 50);
  const treasury = useGameStore(s => s.treasury || 0);

  const clearSummary = () => useGameStore.setState({ lastAcquisitionSummary: null });

  // Closing-success modal
  if (summary && !pending) {
    return <ClosingSuccessModal summary={summary} onClose={clearSummary} t={t} />;
  }

  if (!pending) return null;

  const competitor = COMPETITORS.find(c => c.id === pending.competitorId);
  if (!competitor) return null;
  const stage = ACQUISITION_STAGES.find(s => s.id === pending.stage);
  if (!stage) return null;
  const Icon = STAGE_ICONS[pending.stage] || FileSearch;

  return (
    <div
      className="fixed inset-0 bg-black/75 z-[110] flex items-center justify-center p-4 backdrop-blur-sm"
      data-testid="acquisition-modal"
    >
      <div className="bg-ink-800 rounded-xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto border-2 border-accent/40">
        {/* Header */}
        <div className="px-6 py-4 border-b border-ink-600 flex items-start justify-between gap-3" style={{ background: `linear-gradient(135deg, ${competitor.color}25, ${competitor.color}05)` }}>
          <div className="flex items-center gap-3">
            {competitor.logo ? (
              <img
                src={competitor.logo}
                alt={competitor.name}
                className="w-10 h-10 rounded-lg object-cover flex-shrink-0 border-2"
                style={{ borderColor: competitor.color }}
              />
            ) : (
              <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold text-sm flex-shrink-0" style={{ background: competitor.color }}>
                {competitor.avatar}
              </div>
            )}
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">{t('acq_header_prefix')} — {competitor.name}</p>
              <h2 className="text-lg font-bold text-foreground">{t('acq_stage_label', { current: pending.stage })} — {stage.title}</h2>
            </div>
          </div>
          <button onClick={cancel} className="p-1 rounded hover:bg-white/10 text-slate-400" data-testid="acquisition-cancel" aria-label={t('cancel')}>
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Progress bar */}
        <div className="px-6 py-3 border-b border-ink-700">
          <div className="flex items-center gap-1.5">
            {ACQUISITION_STAGES.map(s => (
              <div
                key={s.id}
                className={`h-1.5 flex-1 rounded-full transition-all ${
                  s.id < pending.stage ? 'bg-emerald-400'
                  : s.id === pending.stage ? 'bg-accent'
                  : 'bg-ink-700'
                }`}
                data-testid={`stage-progress-${s.id}`}
              />
            ))}
          </div>
          <div className="flex items-center gap-1 mt-2 text-[10px] text-slate-500">
            {ACQUISITION_STAGES.map(s => (
              <span key={s.id} className="flex-1 text-center truncate px-1">
                {s.id}. {s.title.split(' ')[0]}
              </span>
            ))}
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4">
          <div className="flex items-start gap-3 p-3 rounded-lg bg-blue-950/30 border border-blue-500/30">
            <Icon className="w-6 h-6 text-blue-300 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-slate-100 leading-relaxed">{stage.description}</p>
          </div>

          {pending.stage === 1 && <DueDiligencePane analysis={pending.analysis} t={t} />}
          {pending.stage === 2 && <LoiPane reputation={reputation} notoriety={useGameStore.getState().notoriety || 0} t={t} />}
          {pending.stage === 3 && <NegotiationPane pending={pending} reputation={reputation} onAdvance={advance} t={t} />}
          {pending.stage === 4 && <AntitrustPane pending={pending} t={t} />}
          {pending.stage === 5 && <ClosingPane pending={pending} treasury={treasury} onComplete={complete} t={t} />}
        </div>

        {/* Footer — for non-stage-3/5 (they have their own CTA inside) */}
        {pending.stage !== 3 && pending.stage !== 5 && (
          <div className="px-6 py-4 border-t border-ink-600 flex items-center justify-between bg-ink-900/40">
            <button onClick={cancel} className="px-3 py-1.5 rounded-lg bg-ink-600 hover:bg-ink-500 text-slate-200 text-xs" data-testid="acquisition-abandon">
              {t('acq_abandon_btn')}
            </button>
            <button
              onClick={() => advance()}
              className="px-5 py-2 rounded-lg bg-accent hover:bg-accent/90 text-white text-sm font-bold flex items-center gap-2"
              data-testid={`stage-${pending.stage}-confirm`}
            >
              {stage.actionLabel} →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function DueDiligencePane({ analysis, competitor, askingPrice, t }) {
  return (
    <div className="space-y-3">
      <h3 className="text-xs uppercase tracking-wider text-slate-400 font-bold">{t('acq_dd_title')}</h3>
      <div className="grid grid-cols-2 gap-2 text-sm">
        <Row label={t('acq_dd_ca')}            value={`${(analysis.ca || 0).toLocaleString('fr-FR')} €`} />
        <Row label={t('acq_dd_net_margin')}    value={`${Math.round((analysis.netMargin || 0) * 100)} %`} />
        <Row label={t('acq_dd_equity')}        value={`${(analysis.equity || 0).toLocaleString('fr-FR')} €`} />
        <Row label={t('acq_dd_net_debt')}      value={`${(analysis.netDebt || 0).toLocaleString('fr-FR')} €`} />
        <Row label={t('acq_dd_fleet')}         value={analysis.fleet} />
        <Row label={t('acq_dd_drivers')}       value={analysis.drivers} />
      </div>
      {analysis.weakness && analysis.weakness.tag !== 'none' && (
        <div className="p-3 rounded-lg bg-amber-950/30 border border-amber-500/30">
          <p className="text-[10px] uppercase tracking-wider text-amber-300 font-bold mb-1">{t('acq_dd_lever_title')}</p>
          <p className="text-sm text-amber-100">{analysis.weakness.label}</p>
          <p className="text-[10px] text-amber-200/70 mt-1">{t('acq_dd_lever_bonus', { pct: Math.round(analysis.weakness.leverage * 100) })}</p>
        </div>
      )}
      {/* LLM-powered DAF strategic commentary (Claude Sonnet 4.5) */}
      <DAFCommentary competitor={competitor} analysis={analysis} askingPrice={askingPrice} />
    </div>
  );
}

function LoiPane({ reputation, notoriety, t }) {
  const score = reputation * 0.6 + notoriety * 0.4;
  const successPct = score >= 70 ? 95 : score >= 50 ? 85 : score >= 30 ? 70 : 50;
  return (
    <div className="p-3 rounded-lg bg-ink-700/40 border border-ink-600 space-y-2">
      <p className="text-sm text-slate-200">
        <MarkdownLite as="span" text={t('acq_loi_rep_line', { reputation, notoriety })} />
      </p>
      <p className="text-xs text-slate-400">
        <MarkdownLite as="span" text={t('acq_loi_chance', { pct: successPct })} />
      </p>
      <p className="text-xs text-amber-200 italic">
        {t('acq_loi_refusal_warning')}
      </p>
    </div>
  );
}

function NegotiationPane({ pending, reputation, onAdvance, t }) {
  const asking = pending.askingPrice;
  const leverage = pending.weaknessLeverage;

  const strategies = [
    { id: 'accept',     labelKey: 'acq_neg_accept_label',     subKey: 'acq_neg_accept_sub',     discount: 0,    cls: 'bg-blue-600 hover:bg-blue-700' },
    { id: 'negotiate',  labelKey: 'acq_neg_negotiate_label',  subKey: 'acq_neg_negotiate_sub',  discount: 0.15, cls: 'bg-amber-600 hover:bg-amber-700' },
    { id: 'aggressive', labelKey: 'acq_neg_aggressive_label', subKey: 'acq_neg_aggressive_sub', discount: 0.25, cls: 'bg-rose-600 hover:bg-rose-700' },
  ];

  return (
    <div className="space-y-3">
      <div className="text-center py-2">
        <p className="text-[10px] uppercase tracking-wider text-slate-500">{t('acq_neg_asking_price')}</p>
        <p className="text-2xl font-mono font-bold text-blue-200">{asking.toLocaleString('fr-FR')} €</p>
      </div>
      <h3 className="text-xs uppercase tracking-wider text-slate-400 font-bold">{t('acq_neg_choose_strategy')}</h3>
      <div className="space-y-2">
        {strategies.map(s => {
          const sim = evaluateNegotiation(asking, s.id, { reputation, weaknessLeverage: leverage, roll: 0.5 });
          const successPct = Math.round(sim.successChance * 100);
          const offerPrice = Math.round(asking * (1 - s.discount));
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => onAdvance(s.id)}
              className={`w-full p-3 rounded-lg text-white text-left transition-colors ${s.cls}`}
              data-testid={`negotiation-${s.id}`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-bold">{t(s.labelKey)}</p>
                  <p className="text-[10px] opacity-80">{t(s.subKey)}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs font-mono font-bold">{offerPrice.toLocaleString('fr-FR')} €</p>
                  <p className="text-[10px] opacity-80">{t('acq_neg_success_label', { pct: successPct })}</p>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function AntitrustPane({ pending, t }) {
  const ourMonthly = useGameStore.getState().monthlyRevenue || 0;
  const compMonthly = pending.analysis?.ca || 0;
  const combined = ((ourMonthly + compMonthly) / 80000) * 100;
  return (
    <div className="space-y-3">
      <div className="p-3 rounded-lg bg-ink-700/40 border border-ink-600 space-y-2">
        <Row label={t('acq_anti_our_ca')}             value={`${ourMonthly.toLocaleString('fr-FR')} €`} />
        <Row label={t('acq_anti_target_ca')}          value={`${compMonthly.toLocaleString('fr-FR')} €`} />
        <Row label={t('acq_anti_combined_share')}     value={`${combined.toFixed(1)} %`} />
      </div>
      <div className={`p-3 rounded-lg border text-xs ${combined < 25 ? 'bg-emerald-950/30 border-emerald-500/40 text-emerald-100' : combined < 40 ? 'bg-amber-950/30 border-amber-500/40 text-amber-100' : combined < 60 ? 'bg-orange-950/30 border-orange-500/40 text-orange-100' : 'bg-rose-950/30 border-rose-500/40 text-rose-100'}`}>
        {combined < 25 && t('acq_anti_low')}
        {combined >= 25 && combined < 40 && t('acq_anti_medium')}
        {combined >= 40 && combined < 60 && t('acq_anti_high')}
        {combined >= 60 && t('acq_anti_dominant')}
      </div>
      <p className="text-[10px] text-slate-500 italic">
        {t('acq_anti_refusal_fee')}
      </p>
    </div>
  );
}

function ClosingPane({ pending, treasury, onComplete, t }) {
  const finalPrice = pending.negotiationOutcome?.offerPrice ?? pending.askingPrice;
  const isAccepted = pending.negotiationOutcome?.isAccepted;
  // 05/02/2026 (Q1 retour user prod) — Manager obligatoire pour finaliser.
  const hiredMgrs = useGameStore(s => s.hiredDepotManagers) || {};
  const { DEPOT_MANAGERS: _MGRS } = require('../../data/depotManagers');
  const availableManagers = _MGRS.filter(m => !hiredMgrs[m.id]);
  const [selectedManagerId, setSelectedManagerId] = useState('');
  const selectedMgr = availableManagers.find(m => m.id === selectedManagerId);
  const managerSigningBonus = selectedMgr?.monthlyCost || 0;
  const totalCost = finalPrice + managerSigningBonus;
  const canAfford = treasury >= totalCost;
  const [clickError, setClickError] = useState('');

  const handleSign = () => {
    setClickError('');
    if (!selectedManagerId) { setClickError('Choisis un responsable de site pour ce dépôt (obligatoire).'); return; } // i18n-keep — error fallback FR-first
    const r = onComplete(selectedManagerId);
    if (r && !r.ok) {
      if (r.reason === 'manager_required') setClickError('Sélectionne un responsable de site.'); // i18n-keep
      else if (r.reason === 'manager_already_hired') setClickError('Ce responsable est déjà recruté ailleurs.'); // i18n-keep
      else if (r.reason === 'insufficient_funds') setClickError(`Trésorerie insuffisante (manque ${(r.missing || (totalCost - treasury)).toLocaleString('fr-FR')} €).`); // i18n-keep
      else setClickError(`Closing refusé (${r.reason}).`); // i18n-keep
    }
  };

  return (
    <div className="space-y-4">
      <div className="p-4 rounded-lg bg-emerald-950/30 border border-emerald-500/40 space-y-2">
        <p className="text-xs uppercase tracking-wider text-emerald-300 font-bold">{t('acq_closing_final_terms')}</p>
        <div className="text-center">
          <p className="text-3xl font-mono font-black text-emerald-100" data-testid="closing-final-price">{finalPrice.toLocaleString('fr-FR')} €</p>
          {pending.negotiationOutcome && (
            <p className="text-[11px] text-emerald-200 mt-1">
              {isAccepted ? t('acq_closing_neg_accepted', { pct: Math.round((pending.negotiationOutcome.discount || 0) * 100) }) : t('acq_closing_neg_refused')}
            </p>
          )}
        </div>
      </div>

      <div className="p-3 rounded-lg bg-ink-700/40 border border-ink-600 text-xs text-slate-300 space-y-1">
        <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">{t('acq_closing_you_get_title')}</p>
        <p>· {t('acq_closing_get_fleet', { count: pending.analysis.fleet })}</p>
        <p>· {t('acq_closing_get_drivers', { count: pending.analysis.drivers })}</p>
        <p>· {t('acq_closing_get_depot')}</p>
        <p>· {t('acq_closing_get_contracts', { count: pending.analysis.contractsActive })}</p>
      </div>

      {/* 05/02/2026 — Sélection du responsable obligatoire avant signature */}
      <div className="p-3 rounded-lg bg-ink-700/40 border border-amber-500/40">
        <p className="text-[10px] uppercase tracking-wider text-amber-300 font-bold mb-2">
          Responsable de site (obligatoire pour reprendre l&apos;activité)
        </p>
        <div className="max-h-44 overflow-y-auto pr-1 space-y-1.5" data-testid="acquisition-manager-list">
          {availableManagers.length === 0 && (
            <p className="text-[11px] text-amber-300 italic">Aucun responsable disponible. Licencie un manager actuel pour libérer un slot.</p> /* i18n-keep — FR-first prod */
          )}
          {availableManagers.map(m => {
            const isSel = selectedManagerId === m.id;
            return (
              <button
                key={m.id}
                type="button"
                onClick={() => setSelectedManagerId(m.id)}
                className={`w-full px-2.5 py-2 rounded-lg border text-left text-xs transition-all ${
                  isSel ? 'border-accent bg-accent/15 text-foreground' : 'border-ink-600 text-slate-300 hover:border-ink-500'
                }`}
                data-testid={`acquisition-manager-${m.id}`}
              >
                <span className="font-semibold flex items-center gap-1.5">
                  {m.name}
                  <span className="text-[10px] px-1 rounded bg-ink-700 text-slate-400 uppercase">{m.seniority}</span>
                </span>
                <span className="block text-[10px] text-slate-500 font-mono">
                  {m.region} · {m.monthlyCost.toLocaleString('fr-FR')} €/m · ★ {m.bonus?.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className={`p-3 rounded-lg border text-xs ${canAfford ? 'bg-blue-950/30 border-blue-500/40 text-blue-100' : 'bg-rose-950/30 border-rose-500/40 text-rose-100'}`}>
        <MarkdownLite text={t('acq_closing_treasury_line', { before: treasury.toLocaleString('fr-FR'), after: (treasury - totalCost).toLocaleString('fr-FR') })} />
        {selectedMgr && (
          <p className="mt-1 text-[10px] text-slate-300">
            Coût total : <span className="font-mono">{finalPrice.toLocaleString('fr-FR')} €</span> rachat + <span className="font-mono">{managerSigningBonus.toLocaleString('fr-FR')} €</span> prime de signature ({selectedMgr.name}) = <strong className="font-mono">{totalCost.toLocaleString('fr-FR')} €</strong>
          </p>
        )}
        {!canAfford && <p className="mt-1">{t('acq_closing_insufficient')}</p>}
      </div>

      {clickError && <p className="text-[11px] text-rose-300 italic">{clickError}</p>}

      <div className="flex justify-end gap-2">
        <button onClick={useGameStore.getState().cancelAcquisition} className="px-4 py-2 rounded-lg bg-ink-600 hover:bg-ink-500 text-slate-200 text-sm" data-testid="closing-cancel">
          {t('acq_closing_postpone_btn')}
        </button>
        <button
          onClick={handleSign}
          disabled={!canAfford || !selectedManagerId}
          className="px-5 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:bg-ink-700 disabled:text-slate-500 disabled:cursor-not-allowed text-white text-sm font-bold flex items-center gap-2"
          data-testid="closing-sign"
        >
          <Banknote className="w-4 h-4" />
          {t('acq_closing_sign_btn', { price: finalPrice.toLocaleString('fr-FR') })}
        </button>
      </div>
    </div>
  );
}

function ClosingSuccessModal({ summary, onClose, t }) {
  return (
    <div className="fixed inset-0 bg-black/85 z-[120] flex items-center justify-center p-4 backdrop-blur-md" data-testid="closing-success-modal">
      <div className="bg-ink-900 rounded-2xl shadow-2xl w-full max-w-xl border-2 border-emerald-500/40 overflow-hidden">
        <div className="px-6 py-8 text-center space-y-3">
          <div className="w-20 h-20 mx-auto rounded-full bg-emerald-500/15 border-2 border-emerald-500/50 flex items-center justify-center">
            <CheckCircle className="w-10 h-10 text-emerald-300" />
          </div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-emerald-300 font-bold">{t('acq_success_label')}</p>
          <h1 className="text-2xl font-display font-black text-foreground">{t('acq_success_welcome', { name: summary.competitorName })}</h1>
          <p className="text-sm text-slate-400">
            <MarkdownLite as="span" text={t('acq_success_finalized', { price: summary.finalPrice.toLocaleString('fr-FR') })} />
          </p>
        </div>

        {summary.competitorId && (
          <div className="px-6 pb-4">
            {/* 10/07/2026 — vignette du dépôt racheté qui rejoint le réseau.
                13/07/2026 — progression unifiée : la scène brandée suit la
                PHASE de développement héritée (summary.depotPhase). */}
            {(() => {
              const inheritedPhase = summary.depotPhase
                || Math.max(2, Math.min(11, 1 + (summary.vehiclesInherited || 0)));
              return (
                <div className="relative rounded-xl overflow-hidden border border-emerald-500/30" data-testid="closing-depot-preview">
                  <img
                    src={depotBrandPhaseImage(summary.competitorId, inheritedPhase)}
                    alt={summary.depotName}
                    className="w-full h-36 object-cover"
                    draggable={false}
                  />
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent px-3 py-2 flex items-center justify-between">
                    <span className="text-[10px] font-bold text-emerald-200 uppercase tracking-wider">{t('acq_success_depot_joins_network')}</span>
                    <span className="text-[10px] font-mono text-amber-200">{t('depot_scene_phase_label', { phase: inheritedPhase })}</span>
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        <div className="px-6 pb-6 space-y-2">
          <Row icon={Building2} label={t('acq_success_new_depot')}            value={summary.depotName} />
          <Row icon={Truck}     label={t('acq_success_vehicles_inherited')}   value={summary.vehiclesInherited} />
          <Row icon={Users}     label={t('acq_success_drivers_followed')}     value={`${summary.driversInherited} / ${summary.driversInherited + summary.driversLeft}`} positive />
          {(summary.contractsInherited || 0) > 0 && (
            <Row icon={TrendingUp} label={t('acq_success_contracts_inherited', { count: summary.contractsInherited })} value={`≈ ${Math.round(summary.contractsMonthlyValue || 0).toLocaleString('fr-FR')} €/mois`} positive />
          )}
          {summary.driversLeft > 0 && (
            <Row icon={TrendingDown} label={t('acq_success_drivers_left', { count: summary.driversLeft })} value="—" negative />
          )}
          <Row icon={TrendingUp} label={t('acq_success_reputation')}  value="+5"  positive />
          <Row icon={TrendingUp} label={t('acq_success_notoriety')}   value="+8"  positive />
        </div>

        <div className="px-6 pb-6">
          <button
            onClick={onClose}
            className="w-full py-3 rounded-lg bg-gradient-to-r from-emerald-600 to-blue-600 hover:from-emerald-700 hover:to-blue-700 text-white font-bold transition-all"
            data-testid="closing-success-close"
          >
            {t('acq_success_access_depot_btn')}
          </button>
        </div>
      </div>
    </div>
  );
}

function Row({ icon: Icon, label, value, positive, negative }) {
  return (
    <div className="flex items-center justify-between p-2 rounded bg-ink-800/50 border border-ink-700">
      <div className="flex items-center gap-2 text-xs text-slate-400">
        {Icon && <Icon className="w-3.5 h-3.5" />}
        <span>{label}</span>
      </div>
      <span className={`text-sm font-mono font-bold ${positive ? 'text-emerald-300' : negative ? 'text-rose-300' : 'text-foreground'}`}>{value}</span>
    </div>
  );
}
